/* eslint-disable multiline-ternary */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { type RegionResponse } from '../../api/data/RegionResponse'
import { useGetRegions } from '../../api/hooks'
import { customerInfoVMMapper, customerInformationMapper, type CustomerInfoVM } from '../../models/CustomerInfoVM'
import { getConfig } from '../../services/config-service'
import { getRegionsAction, reviewReservationAction, setStellantisParentNotifyCustomerInfoClick } from '../../store/reservationSlice'
import { type RootState } from '../../store/store'
import styles from './CustomerInfoTab.module.scss'
import { getOEMName } from '../../modules/auth/helpers'
import { sendStellantisCustomerInfoStartMessage } from '../../services/notify-service'

const CustomerInfoTab = (): any => {
  const reservation = useSelector((state: RootState) => state.reservationSlice.reservation)
  const regionsData: undefined | RegionResponse[] = useSelector((state: RootState) => state.reservationSlice.regionsData)
  // INITIAL PARENT POSTMESSAGE CONTROL FLAG
  const stellantisParentNotifyCustomerInfoClick: boolean = useSelector((state: RootState) => state.reservationSlice.stellantisParentNotifyCustomerInfoClick)
  const refreshRegions = typeof regionsData === 'undefined'
  const { isLoading, error, regions } = useGetRegions(refreshRegions)
  const [patronMinAgeConfig, setPatronMinAgeConfig] = useState(0)
  const configValue = getConfig<number>('patronMinAge', false)
  const [insurangeToggleStatus, setInsurangeToggleStatus] = useState(false)
  const currentOem = getOEMName()
  useEffect(() => {
    setInsurangeToggleStatus(getValues('insuranceToggle'))
    if (regions !== null && regions?.statusCode === 200 && regions?.data !== null && regions?.data.length > 0) {
      dispatch(getRegionsAction(regions.data))
    }
    setPatronMinAgeConfig(configValue ?? 0)
    if (insurangeToggleStatus) {
      register('insuranceImage', { required: true })
      reset({ ...getValues(), insuranceCompany: undefined, policyNumber: undefined, insuranceExpDate: undefined })
      unregister(['insuranceCompany', 'policyNumber', 'insuranceExpDate'])
    } else {
      register('insuranceCompany', { required: true })
      register('policyNumber', { required: true })
      register('insuranceExpDate', { required: true })

      reset({ ...getValues(), insuranceImage: undefined })
      unregister(['insuranceImage'])
    }
  }, [regions, configValue])

  const dispatch = useDispatch()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    reset,
    unregister,
    setError
  } = useForm<CustomerInfoVM>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    values: customerInfoVMMapper(reservation),
    shouldUnregister: true
  })

  const onSubmit = async (data: CustomerInfoVM): Promise<any> => {
    if (insurangeToggleStatus && getValues('insuranceImage').length === 0) {
      setError('insuranceImage', { type: 'custom', message: 'Photo of your insurance card is required' })
    } else {
      await handleNext(data)
    }
  }

  async function handleNext(data: CustomerInfoVM): Promise<void> {
    const customer = customerInformationMapper(data)
    const updtReservation = {
      ...reservation,
      customer
    }
    dispatch(reviewReservationAction(updtReservation))
  }
  async function postStellantisMessageOnFirstClick(): Promise<void> {
    // If oem is Stellantis, post a message to the parent window indicating customer info started
    // sendStellantisCustomerInfoStartMessage()
    if (currentOem?.toUpperCase().trim() === 'FCA GROUP' && !stellantisParentNotifyCustomerInfoClick) {
      sendStellantisCustomerInfoStartMessage()
      dispatch(setStellantisParentNotifyCustomerInfoClick(true))
    }
  }
  return (
    <div className={styles.CustomerInfoDtoTab} data-testid='CustomerInfoTab'>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className='grid-x grid-margin-x'>
            <div className='cell'>
              <fieldset className='fieldset'>
                <legend><span className="material-symbols-outlined">person</span> Customer Info</legend>
                <div className='grid-x grid-margin-x' onClick={postStellantisMessageOnFirstClick}>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='firstName' className={styles.required}>
                      {'First Name'}
                    </label>
                    <input
                      id='firstName'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.firstName?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('firstName', { required: 'First Name is required' })}
                    />
                    <span className='form-error is-visible'>{errors.firstName?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='lastName' className={styles.required}>
                      {'Last Name'}
                    </label>
                    <input
                      id='lastName'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.lastName?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('lastName', { required: 'Last Name is required' })}
                    />
                    <span className='form-error is-visible'>{errors.lastName?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='email' className={styles.required}>
                      {'Email'}
                    </label>
                    <input
                      id='email'
                      type='email'
                      disabled={isSubmitting}
                      className={errors.email?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: 'Email format is invalid'
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.email?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='phone' className={styles.required}>
                      {'Phone'}
                    </label>
                    <input
                      id='phone'
                      type='tel'
                      disabled={isSubmitting}
                      className={errors.phone?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('phone', {
                        required: 'Phone is required',
                        pattern: {
                          value: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                          message: 'Phone format is invalid'
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.phone?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='vehicleMake' className={styles.required}>
                      {'Personal Vehicle Make'}
                    </label>
                    <input
                      id='vehicleMake'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.vehicleMake?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('vehicleMake', { required: 'Personal Vehicle Make is required' })}
                    />
                    <span className='form-error is-visible'>{errors.vehicleMake?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='vehicleModel' className={styles.required}>
                      {'Personal Vehicle Model'}
                    </label>
                    <input
                      id='vehicleModel'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.vehicleModel?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('vehicleModel', { required: 'Personal Vehicle Model is required' })}
                    />
                    <span className='form-error is-visible'>{errors.vehicleModel?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='vehicleYear' className={styles.required}>
                      {'Personal Vehicle Year'}
                    </label>
                    <input
                      id='vehicleYear'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.vehicleYear?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('vehicleYear', {
                        required: 'Personal Vehicle Year is required',
                        validate: (value) => {
                          const re = /^[12]\d{3}$/
                          return value === '' || re.test(value) || 'Personal Vehicle Year value is invalid'
                        }
                      })
                      }
                    />
                    <span className='form-error is-visible'>{errors.vehicleYear?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='VIN'>
                      {'Personal VIN'}
                    </label>
                    <input
                      id='VIN'
                      type='text'
                      disabled={isSubmitting}
                      {...register('VIN')}
                    />
                  </div>
                  <div className='cell small-12 medium-6'>
                    <input id='statusCheckMessage' type='checkbox' {...register('statusCheckMessage')} />
                    <label htmlFor='statusCheckMessage'>{'Opt-in to text message notifications'}</label>
                  </div>
                </div>
              </fieldset>
            </div>
            <div className='cell small-12 medium-6'>
              <fieldset className='fieldset'>
                <legend><span className="material-symbols-outlined">home</span> Home Address</legend>
                <div className='grid-x grid-margin-x'>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='homeAddress1' className={styles.required}>
                      {'Address Line 1'}
                    </label>
                    <input
                      id='homeAddress1'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.homeAddress1?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('homeAddress1', { required: 'Address Line 1 is required' })}
                    />
                    <span className='form-error is-visible'>{errors.homeAddress1?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='homeAddress2'>{'Address Line 2'}</label>
                    <input id='homeAddress2' type='text' disabled={isSubmitting} {...register('homeAddress2')} />
                    <span className='form-error is-visible'>{errors.homeAddress2?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='homeCity' className={styles.required}>
                      {'City/Town'}
                    </label>
                    <input
                      id='homeCity'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.homeCity?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('homeCity', { required: 'City/Town is required' })}
                    />
                    <span className='form-error is-visible'>{errors.homeCity?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='homeState' className={styles.required}>
                      {'State'}
                    </label>
                    <select
                      id='homeState'
                      className={errors.homeState?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('homeState', { required: 'State is required' })}
                    >
                      {(regionsData ?? regions.data).map((item: RegionResponse) => (
                        <option key={item.regionId} value={item.regionCode}>
                          {item.regionName}
                        </option>
                      ))}
                    </select>
                    <span className='form-error is-visible'>{errors.homeState?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='homeZip' className={styles.required}>
                      {'Zip'}
                    </label>
                    <input
                      id='homeZip'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.homeZip?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('homeZip', { required: 'Zip is required' })}
                    />
                    <span className='form-error is-visible'>{errors.homeZip?.message}</span>
                  </div>
                </div>
              </fieldset>
            </div>
            <div className='cell small-12 medium-6'>
              <fieldset className='fieldset'>
                <legend><span className="material-symbols-outlined">id_card</span> Drivers License</legend>
                <div className='grid-x grid-margin-x'>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='licenseNumber' className={styles.required}>
                      {'License Number'}
                    </label>
                    <input
                      id='licenseNumber'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.licenseNumber?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('licenseNumber', { required: 'License Number is required' })}
                    />
                    <span className='form-error is-visible'>{errors.licenseNumber?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='licenseState' className={styles.required}>
                      {'State'}
                    </label>
                    <select
                      id='licenseState'
                      className={errors.licenseState?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('licenseState', { required: 'State is required' })}
                    >
                      {(regionsData ?? regions.data).map((item: RegionResponse) => (
                        <option key={item.regionId} value={item.regionCode}>
                          {item.regionName}
                        </option>
                      ))}
                    </select>
                    <span className='form-error is-visible'>{errors.licenseState?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='licenseExpDate' className={styles.required}>
                      {'Expiration Date'}
                    </label>
                    <input
                      type='date'
                      id='licenseExpDate'
                      className={errors.licenseExpDate?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('licenseExpDate', {
                        required: 'Expiration Date is required',
                        validate: (value) => {
                          const selectedDate = new Date(value)
                          const today = new Date()
                          today.setHours(0, 0, 0, 0)
                          return selectedDate > today || "Driver's License Expired"
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.licenseExpDate?.message}</span>
                  </div>
                  <div className='cell small-12 medium-6'>
                    <label htmlFor='dateOfBirth' className={styles.required}>
                      {'Date of Birth'}
                    </label>
                    <input
                      type='date'
                      id='dateOfBirth'
                      className={errors.dateOfBirth?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('dateOfBirth', {
                        required: 'Date of Birth is required',
                        validate: (value) => {
                          const calculateAge = function (birthDay: number, birthMonth: number, birthYear: number): number {
                            const todayDate = new Date()
                            const todayYear = todayDate.getFullYear()
                            const todayMonth = todayDate.getMonth()
                            const todayDay = todayDate.getDate()
                            let age = todayYear - birthYear
                            if (todayMonth < birthMonth - 1) {
                              age--
                            }
                            if (birthMonth - 1 === todayMonth && todayDay < birthDay) {
                              age--
                            }
                            return age
                          }
                          if (value !== null) {
                            const theDate = new Date(value)
                            const age = calculateAge(theDate.getDate(), theDate.getMonth() + 1, theDate.getFullYear())
                            return age >= patronMinAgeConfig || 'Customer is under the minimum age'
                          }
                          return 'Customer is under the minimum age'
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.dateOfBirth?.message}</span>
                  </div>
                </div>
              </fieldset>
            </div>
            <div className='cell small-12 medium-6'>
              <fieldset className='fieldset'>
                <legend><span className="material-symbols-outlined">description</span> Insurance Info</legend>
                <div className='grid-x grid-margin-x'>
                  <div className='cell small-12'>
                    <div className="cell">
                      <label htmlFor="ins-name">{'Upload Photo'}</label>
                    </div>
                    <div className="switch small">
                      <input
                        data-toggle="insurance-method"
                        id="exampleSwitch"
                        type="checkbox"
                        disabled={isSubmitting}
                        className="switch-input"
                        {...register('insuranceToggle', {
                          onChange(value) {
                            const insuranceToggleValue = getValues('insuranceToggle')
                            setInsurangeToggleStatus(insuranceToggleValue)
                          }
                        })} />
                      <label className="switch-paddle" htmlFor="exampleSwitch">
                        <span className="show-for-sr">Download Kittens</span>
                      </label>
                    </div>
                  </div>
                  { !insurangeToggleStatus ? <div className="cell small-12 medium-6">
                    <label htmlFor='insuranceCompany' className={styles.required}>
                      {'Insurance Company'}
                    </label>
                    <input
                      id='insuranceCompany'
                      type='text'
                      disabled={isSubmitting}
                      className={`${errors.insuranceCompany?.message !== undefined ? 'is-invalid-input' : ''} ${styles.dropdownmargin}`}
                      {...register('insuranceCompany', {
                        validate: (value) => {
                          const insuranceCompany = getValues('insuranceCompany')
                          if (insuranceCompany.length === 0 && value === '') {
                            return 'Insurance Company is required'
                          }
                          if (insuranceCompany.length !== 0) return true
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.insuranceCompany?.message}</span>
                  </div> : ''}
                  { !insurangeToggleStatus ? <div className='cell small-12 medium-6'>
                    <label htmlFor='policyNumber' className={styles.required}>
                      {'Policy Number'}
                    </label>
                    <input
                      id='policyNumber'
                      type='text'
                      disabled={isSubmitting}
                      className={errors.policyNumber?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('policyNumber', {
                        validate: (value) => {
                          const policyNumber = getValues('policyNumber')
                          if (policyNumber.length === 0 && value === '') {
                            return 'Policy Number is required'
                          }
                          if (policyNumber.length !== 0) return true
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.policyNumber?.message}</span>
                  </div> : ''}
                  { !insurangeToggleStatus ? <div className='cell small-12 medium-6'>
                    <label htmlFor='insuranceExpDate' className={styles.required}>
                      {'Expiration Date'}
                    </label>
                    <input
                      type='date'
                      id='insuranceExpDate'
                      className={errors.insuranceExpDate?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('insuranceExpDate', {
                        validate: (value) => {
                          const insuranceExpDate = getValues('insuranceExpDate')
                          if (insuranceExpDate.length === 0 && value.toString() === '') {
                            return 'Expiration Date is required'
                          }
                          if (insuranceExpDate.length !== 0) return true
                          const selectedDate = new Date(value)
                          const today = new Date()
                          today.setHours(0, 0, 0, 0)
                          return selectedDate > today || 'Insurance Expired'
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.insuranceExpDate?.message}</span>
                  </div> : ''}
                  { insurangeToggleStatus ? <div className='cell small-12 medium-6'>
                    <label htmlFor='insuranceImage' className={styles.required}>
                      {'Upload a clear photo of your insurance card'}
                    </label>
                    <input
                      type='file'
                      id='insuranceImage'
                      accept='image/*'
                      className={errors.insuranceImage?.message !== undefined ? 'is-invalid-input' : ''}
                      {...register('insuranceImage', {
                        async onChange(event) {
                          const file = event.target.files[0]
                          if (file !== null) {
                            const reader = new FileReader()
                            reader.onload = () => {
                              setValue('insuranceCard', reader.result?.toString() ?? '')
                            }
                            reader.readAsDataURL(file)
                          }
                        }
                      })}
                    />
                    <span className='form-error is-visible'>{errors.insuranceImage?.message}</span>

                    <button
                      className='hollow button'
                      onClick={(event) => {
                        setValue('insuranceImage', '')
                        event.preventDefault()
                      }}
                    >
                      {'Delete'}
                    </button>
                  </div> : ''}
                </div>
              </fieldset>
            </div>
          </div>
          <button disabled={isSubmitting} className='button primary float-right expanded' data-testid='Button' type='submit'>
            {'NEXT'}
          </button>
        </form>
      )}
      {regions.errorMessage !== '' || error !== '' ? <p className='errorMessage'>{regions.errorMessage ?? error}</p> : ''}
    </div>
  )
}

export default CustomerInfoTab
